import React from 'react';
import { Plus, X, CalendarOff } from 'lucide-react';
import { DateField } from './ui/DateTimeField';

/**
 * SAM-546 (N11) — one-off closures / holidays editor. New Tailwind component
 * (SAM-175), mounted from ServiceDetailDrawer.tsx.
 *
 * Maps to `service.restrictions.availability` — the SAME column/shape
 * `functions/booking/lambda_function.py::update_service_availability` already
 * writes (`{start, end, status, reason}`, `status` "available"|"unavailable"),
 * so this editor is additive on an existing field, not a new one. Only
 * `status: 'unavailable'` periods are shown/created here (the only kind
 * `engine_sync.py`'s `closures_from_restrictions` turns into an engine
 * closure block); any other period already on the service is left untouched
 * on save (see ServiceDetailDrawer's formToServiceData).
 *
 * Dates are day-granularity ("YYYY-MM-DD") — the same granularity the
 * backend's closure block covers (`_closure_block_window` treats a closure
 * as whole calendar days).
 */

export interface ClosureForm {
  start_date: string;
  end_date: string;
  reason: string;
}

interface ServiceClosuresEditorProps {
  closures: ClosureForm[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (index: number, field: keyof ClosureForm, value: string) => void;
}

export const ServiceClosuresEditor: React.FC<ServiceClosuresEditorProps> = ({
  closures,
  onAdd,
  onRemove,
  onChange,
}) => {
  const invalidIndexes = new Set(
    closures
      .map((c, i) => (c.start_date && c.end_date && c.end_date < c.start_date ? i : -1))
      .filter((i) => i >= 0)
  );

  return (
    <div className="text-sm">
      <p className="text-xs mb-3" style={{ color: 'var(--ink-500)' }}>
        Full-day closures — holidays, maintenance, anything that blocks bookings regardless of the
        weekly hours above. Applies to every resource of this service (per-resource closures
        aren't wired to the engine yet).
      </p>

      {closures.length === 0 ? (
        <p className="text-xs italic mb-3" style={{ color: 'var(--ink-400)' }}>
          No closures — this service follows Operating Hours every week with no exceptions.
        </p>
      ) : (
        <div className="space-y-2 mb-3">
          {closures.map((closure, index) => (
            <div
              key={index}
              className="rounded-lg p-3"
              style={{ border: '1px solid var(--line)', background: 'var(--surface-2)' }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <CalendarOff size={12} style={{ color: 'var(--ink-400)' }} />
                  <span className="text-[11px]" style={{ color: 'var(--ink-500)' }}>Closure {index + 1}</span>
                </div>
                <button
                  type="button"
                  aria-label={`Remove closure ${index + 1}`}
                  onClick={() => onRemove(index)}
                  className="rounded hover:opacity-70"
                  style={{ color: 'var(--ink-400)' }}
                >
                  <X size={13} />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <DateField
                  label="From" ariaLabel={`Closure ${index + 1} start date`} value={closure.start_date}
                  onChange={(v) => onChange(index, 'start_date', v)} invalid={invalidIndexes.has(index)}
                />
                <DateField
                  label="To (inclusive)" ariaLabel={`Closure ${index + 1} end date`} value={closure.end_date}
                  onChange={(v) => onChange(index, 'end_date', v)} invalid={invalidIndexes.has(index)}
                />
                <label className="flex flex-col gap-1">
                  <span className="text-[11px]" style={{ color: 'var(--ink-500)' }}>Reason (optional)</span>
                  <input
                    type="text"
                    value={closure.reason}
                    onChange={(e) => onChange(index, 'reason', e.target.value)}
                    placeholder="e.g. Holiday"
                    className="rounded px-2 py-1 text-xs"
                    style={{ border: '1px solid var(--line)' }}
                  />
                </label>
              </div>
              {invalidIndexes.has(index) && (
                <p className="text-[11px] mt-1" style={{ color: '#DC2626' }}>
                  "To" must be on or after "From".
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      <button type="button" onClick={onAdd} className="btn" style={{ width: '100%', justifyContent: 'center' }}>
        <Plus size={13} />
        Add closure
      </button>
    </div>
  );
};
